// Sub-tab A — "נכסים במיניסייט": the contact's curated listing set (mock s18 +
// Haim's review rounds). Live view of the favorite-listings-of-contact doc +
// listing-page-views engagement, with:
//  - stats mini-cards (נצפו/טרם נצפו/אהבו/לא רלוונטי) that apply a filter AND
//    highlight the matching cards; semantic colors are fixed (Haim runtime round):
//    אהבו=success(green), לא רלוונטי=error(red), נצפו=info(blue) — consistent
//    across stat cards, filter chips and card badges;
//  - watermark REALITY: clean (watermark-free) versions display for listings THIS
//    user performed removal on (per-user gate over the shared cache; no toggle —
//    Haim reject rounds), from the LIVE status data (never the stale cached seed) —
//    card thumbs and quick view swap exactly like the public client does; images
//    without a clean version keep showing the original (watermarked);
//  - a PER-LISTING watermark affordance on every card (badge on the photo + an action
//    button), opening the existing WatermarkRemovalDialog/Studio for that ONE listing —
//    the bulk "several listings at once" run in the tab header is an addition, never the
//    only way in (Haim: "remove watermark on pictures from specific listings only");
//  - engagement filter chips + an instant client-side fuzzy search bar;
//  - ONE WhatsApp follow-up action per card ("הי X, מה עם הנכס הזה?" + deep link);
//  - undo-able removal: the card lingers ~3s with a pastel border and a החזרה
//    button; the Firestore write commits only after the window (flushed
//    immediately on unmount/page close) so per-listing edits survive an undo;
//  - listing quick-view on card click + the per-listing edit dialog.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  Button,
  Tooltip,
  Skeleton,
  TextField,
  IconButton,
  Typography,
  ButtonBase,
  InputAdornment
} from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import {
  Add,
  Edit,
  Search,
  Replay,
  Language,
  WhatsApp,
  OpenInNew,
  AutoFixHigh,
  DeleteOutline
} from "@material-ui/icons";
import { useSnackbar } from "notistack5";
import useFirestore from "hooks/useFirestore";
import Label from "components/Label";
import CopyClipboard from "components/CopyClipboard";
import { WatermarkRemovalDialog } from "components/_dashboard/watermarks/WatermarkRemovalDialog";
import { getUserFirstNameFallBackToPhoneNumber } from "utils/Haim/utils";
import {
  pickListingDisplayCoverUrl,
  applyWatermarkFreeImagesToListing
} from "utils/listings/watermarkFree";
import type {
  RealEstateContact,
  ListingPageViewsDocument,
  FavoriteListingsOfContact
} from "../../../../../../@types/sharedSchema";
import type { MatchHit } from "./matchesSearch";
import type { ListingWatermarkStatus } from "./useMatchesWatermarkStatus";
import { removeListingFromContactMinisite } from "./matchesData";
import {
  buildMinisiteUrl,
  openWhatsAppShare,
  buildMinisiteListingUrl,
  buildMinisiteShareMessage,
  buildListingFollowUpMessage
} from "./matchesShare";
import {
  formatRelativeDayTime,
  buildListingMetaLine,
  buildListingAddressLine
} from "./matchesDisplay";
import { fuzzyMatches, buildListingSearchHaystack } from "./matchesFuzzy";
import ListingPriceWithHistory from "./ListingPriceWithHistory";
import { ListingOrgActivityIndicator } from "components/_dashboard/listings/orgActivity/ListingOrgActivityIndicator";
import SellerImageOverlay from "./SellerImageOverlay";
import ListingQuickViewDialog from "./ListingQuickViewDialog";
import ListingEditDialog from "./ListingEditDialog";
import { useContactSurfaceDown } from "../../contactSurface";

const REMOVE_UNDO_WINDOW_MS = 3000;

type MinisiteSubTabProps = {
  contact: RealEstateContact;
  /** undefined = still loading; null = no minisite doc yet. */
  favorites: FavoriteListingsOfContact | null | undefined;
  views: ListingPageViewsDocument | null | undefined;
  /** Listing data for the favorites' listingIds, newest-added first. */
  listings: MatchHit[] | undefined;
  /**
   * Live per-listing watermark truth, computed once for the whole set in MatchesTab
   * (useMatchesWatermarkStatus). Passed down rather than recomputed so the card badge,
   * the header chip and the multi-listing dialog can never disagree — and so we don't
   * open a second snapshot listener per card.
   */
  watermarkStatuses?: ListingWatermarkStatus[];
  onSwitchToAdd: () => void;
};

// ── engagement model ────────────────────────────────────────────────────────────

type EngagementFilterId = "all" | "viewed" | "unviewed" | "liked" | "disliked" | "visited";

type PaletteKey = "success" | "warning" | "error" | "info" | "primary";

// Semantic color contract (Haim runtime round): אהבו=success, לא רלוונטי=error,
// נצפו=info. Applied everywhere an engagement state is colored — stat mini-cards,
// filter chips, card badges and the active-filter card highlight.
const ENGAGEMENT_FILTERS: { id: EngagementFilterId; label: string; palette?: PaletteKey }[] = [
  { id: "all", label: "הכל" },
  { id: "viewed", label: "נצפו", palette: "info" },
  { id: "unviewed", label: "טרם נצפו", palette: "warning" },
  { id: "liked", label: "אהבו", palette: "success" },
  { id: "disliked", label: "לא רלוונטי", palette: "error" },
  { id: "visited", label: "ביקרו בנכס", palette: "primary" }
];

function listingViewCount(
  listingId: string,
  views: ListingPageViewsDocument | null | undefined
): number {
  return views?.listingDetailViewCounts?.[listingId] ?? 0;
}

function lastListingViewAt(
  listingId: string,
  views: ListingPageViewsDocument | null | undefined
): Date | undefined {
  const events = views?.recentViewEvents ?? [];
  for (let i = events.length - 1; i >= 0; i -= 1) {
    if (events[i]?.listingId === listingId) {
      const ts = events[i].timestamp;
      return ts instanceof Date ? ts : new Date(ts as unknown as string);
    }
  }
  return undefined;
}

type ListingEngagement = {
  viewed: boolean;
  viewCount: number;
  liked: boolean;
  disliked: boolean;
  visited: boolean;
};

function getListingEngagement(
  listingId: string,
  favorites: FavoriteListingsOfContact | null | undefined,
  views: ListingPageViewsDocument | null | undefined
): ListingEngagement {
  const viewCount = listingViewCount(listingId, views);
  return {
    viewed: viewCount > 0,
    viewCount,
    liked: Boolean(favorites?.listingIdsUserLiked?.includes(listingId)),
    disliked: Boolean(favorites?.listingIdsUserDisliked?.includes(listingId)),
    visited: Boolean(favorites?.listingIdsUserVisited?.includes(listingId))
  };
}

function engagementMatchesFilter(e: ListingEngagement, filter: EngagementFilterId): boolean {
  switch (filter) {
    case "viewed":
      return e.viewed;
    case "unviewed":
      return !e.viewed;
    case "liked":
      return e.liked;
    case "disliked":
      return e.disliked;
    case "visited":
      return e.visited;
    default:
      return true;
  }
}

// ── stat mini-card ──────────────────────────────────────────────────────────────

function StatCard({
  label,
  count,
  palette,
  active,
  onClick
}: {
  label: string;
  count: number;
  palette: PaletteKey;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  const theme = useTheme();
  const color = theme.palette[palette].main;
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        flex: 1,
        minWidth: 88,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.25,
        bgcolor: alpha(color, active ? 0.16 : 0.08),
        border: `1px solid ${alpha(color, active ? 0.6 : 0.16)}`,
        "&:hover": { bgcolor: alpha(color, 0.16) }
      }}
    >
      <Typography variant="h6" sx={{ lineHeight: 1, color }}>
        {count}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </ButtonBase>
  );
}

// ── listing card ────────────────────────────────────────────────────────────────

const listingThumbSx = (imageUrl: string | undefined) => ({
  position: "relative" as const,
  height: 128,
  borderRadius: "8px 8px 0 0",
  bgcolor: "grey.200",
  backgroundImage: imageUrl
    ? `url(${imageUrl})`
    : "linear-gradient(135deg, #dfe3e8 0%, #c4cdd5 100%)",
  backgroundSize: "cover",
  backgroundPosition: "center"
});

function MinisiteListingCard({
  listing,
  contact,
  favorites,
  views,
  minisiteUrl,
  highlightPalette,
  pendingRemoval,
  watermarkStatus,
  onOpen,
  onEdit,
  onRemove,
  onUndoRemove,
  onManageWatermarks
}: {
  listing: MatchHit;
  contact: RealEstateContact;
  favorites: FavoriteListingsOfContact;
  views: ListingPageViewsDocument | null | undefined;
  minisiteUrl: string;
  /** Set while an engagement filter is active — colors the matching cards' border. */
  highlightPalette: PaletteKey | undefined;
  pendingRemoval: boolean;
  /** Live watermark truth for THIS listing; undefined while the set is still loading. */
  watermarkStatus: ListingWatermarkStatus | undefined;
  onOpen: (listing: MatchHit) => void;
  onEdit: (listing: MatchHit) => void;
  onRemove: (listingId: string) => void;
  onUndoRemove: (listingId: string) => void;
  onManageWatermarks: (listingId: string) => void;
}): JSX.Element {
  const theme = useTheme();
  const listingId = listing.objectID;

  const engagement = getListingEngagement(listingId, favorites, views);
  const realtorNote = favorites.additionalDetails?.[listingId]?.listingNotes?.trim();
  const lastViewedAt = lastListingViewAt(listingId, views);
  const addressLine = buildListingAddressLine(listing);
  const { userSettings } = useFirestore();
  const termsAccepted = Boolean(userSettings?.watermarkTermsAcceptedAt);
  // Photo truth comes from the LIVE status, never the cached seed (which is a
  // point-in-time read — a removal that just finished must flip the thumb now).
  // pickListingDisplayCoverUrl also handles the cover trap: coverImageUrl is stored
  // at its own path and never gains a clean version itself, so the clean FIRST
  // gallery image (same photo) stands in for it.
  const watermarkDisplayListing: MatchHit = {
    ...listing,
    // The shared cache, gated only on the realtor's rights acceptance — there is no per-listing
    // gate any more (see utils/listings/watermarkFree.ts).
    watermarkFreeImages: termsAccepted ? watermarkStatus?.watermarkFreeImages : []
  };
  const thumbUrl = pickListingDisplayCoverUrl(watermarkDisplayListing);

  // Per-listing watermark state. `tracked` false = nothing removal could act on (no
  // marketplace doc / no images) — then we show no affordance at all rather than a
  // button that leads to an empty studio.
  const watermarkTracked = Boolean(watermarkStatus?.tracked);
  const watermarkPendingCount = watermarkStatus?.pendingUrls.length ?? 0;
  const watermarkAllClean = watermarkTracked && watermarkPendingCount === 0;
  // A watermark WARNING is a claim about the photos, and it is only true for scraped
  // market listings — the realtor's own photos are theirs already. So an own listing
  // keeps the action (removal stays one click away) but loses the alarm: no orange
  // "סימני מים ×5" badge on the photo, and a neutral icon instead of a warning one.
  const watermarkIsMarket = Boolean(watermarkStatus?.isMarketListing);
  const watermarkActionColor = watermarkAllClean
    ? "success"
    : watermarkIsMarket
    ? "warning"
    : "default";
  const watermarkTooltip = watermarkAllClean
    ? `כל ${watermarkStatus?.totalCount ?? 0} התמונות של הנכס כבר ללא סימני מים — צפייה בתוצאות`
    : watermarkIsMarket
    ? `הסרת סימני מים לנכס הזה (${watermarkPendingCount} מתוך ${
        watermarkStatus?.totalCount ?? 0
      } תמונות ממתינות)`
    : `הנכס שלכם — בדרך כלל אין צורך בהסרת סימני מים. אפשר בכל זאת להסיר (${watermarkPendingCount} תמונות)`;

  const borderColor = pendingRemoval
    ? alpha(theme.palette.warning.main, 0.7)
    : highlightPalette
    ? alpha(theme.palette[highlightPalette].main, 0.55)
    : theme.palette.divider;

  const handleWhatsApp = (): void => {
    const listingUrl = favorites.id
      ? buildMinisiteListingUrl(favorites.id, listingId)
      : minisiteUrl;
    openWhatsAppShare(contact, buildListingFollowUpMessage(contact, addressLine, listingUrl));
  };

  return (
    <Box
      onClick={pendingRemoval ? undefined : () => onOpen(listing)}
      sx={{
        borderRadius: 1,
        overflow: "hidden",
        border: `1.5px solid ${borderColor}`,
        bgcolor: pendingRemoval
          ? alpha(theme.palette.warning.main, 0.06)
          : "background.paper",
        display: "flex",
        flexDirection: "column",
        cursor: pendingRemoval ? "default" : "pointer",
        opacity: pendingRemoval ? 0.85 : 1,
        transition: "border-color 150ms, opacity 150ms"
      }}
    >
      <Box sx={listingThumbSx(thumbUrl)}>
        {/* rendered first so the engagement badges (later siblings) paint above it */}
        <SellerImageOverlay sellerName={listing.sellerName} agencyName={listing.agencyName} />
        <Box sx={{ position: "absolute", top: 8, insetInlineStart: 8 }}>
          {engagement.viewed ? (
            <Label color="info" variant="filled">
              👁 נצפה{engagement.viewCount > 1 ? ` ×${engagement.viewCount}` : ""}
            </Label>
          ) : (
            <Label color="warning" variant="filled">
              טרם נצפה
            </Label>
          )}
        </Box>
        {engagement.visited && (
          <Box sx={{ position: "absolute", bottom: 8, insetInlineStart: 8 }}>
            <Label color="primary" variant="filled">
              ביקר בנכס
            </Label>
          </Box>
        )}
        {/* Watermark badge — the at-a-glance answer to "does THIS listing still have
            watermarks?", and a one-click way into removal for this listing only.
            Bottom-end corner: top-end carries the seller strip, bottom-start the
            "visited" label. Only rendered when there is something to SAY: a market
            listing with pending images, or any listing this user already cleaned. An
            own listing that was never cleaned gets no badge — claiming its photos have
            watermarks would be inventing a problem — but keeps the toolbar action. */}
        {watermarkTracked && !pendingRemoval && (watermarkAllClean || watermarkIsMarket) && (
          <Tooltip title={watermarkTooltip}>
            <Box
              sx={{ position: "absolute", bottom: 8, insetInlineEnd: 8, cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                onManageWatermarks(listingId);
              }}
            >
              <Label color={watermarkAllClean ? "success" : "warning"} variant="filled">
                {watermarkAllClean ? "✓ ללא סימני מים" : `סימני מים ×${watermarkPendingCount}`}
              </Label>
            </Box>
          </Tooltip>
        )}
      </Box>

      <Stack spacing={0.25} sx={{ p: 1.5, flex: 1 }}>
        <Typography variant="subtitle2" noWrap title={addressLine}>
          {addressLine}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {buildListingMetaLine(listing)}
        </Typography>
        <Box>
          <ListingPriceWithHistory
            price={listing.price}
            priceHistory={listing.priceHistory}
            lastPriceChangeDate={listing.lastPriceChangeDate}
            variant="subtitle1"
          />
        </Box>
        {realtorNote && (
          <Typography variant="caption" color="text.secondary" noWrap title={realtorNote}>
            📝 {realtorNote}
          </Typography>
        )}

        {/* Bottom row = engagement meta + actions, with opposite shrink rules on
            purpose. `Label` is nowrap inline-flex with its own min-width, so under
            pressure it shrank BELOW its text (the pill collapsing while "לא רלוונטי"
            spilled across the icons) and, narrower still, the last icon buttons were
            sliced off by the card's overflow:hidden — measured at every card width
            under ~270px, which is most of them. Now the row wraps, the meta group
            truncates, and the actions never shrink or clip.
            Same marginInlineStart:auto idiom as notes/NoteComposer + tasks/TaskQuickAdd:
            end-aligned in RTL without stylis flipping it, and pinned to the end of
            their own line once the row wraps. */}
        <Stack
          direction="row"
          alignItems="center"
          gap={0.75}
          sx={{ pt: 0.5, minHeight: 34, flexWrap: "wrap" }}
        >
          {(lastViewedAt || engagement.liked || engagement.disliked) && (
            <Stack
              direction="row"
              alignItems="center"
              gap={0.75}
              sx={{ minWidth: 0, flex: "1 1 auto", overflow: "hidden" }}
            >
              {lastViewedAt && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {formatRelativeDayTime(lastViewedAt)}
                </Typography>
              )}
              {engagement.liked && <Label color="success">❤ אהבו</Label>}
              {!engagement.liked && engagement.disliked && (
                <Label color="error">לא רלוונטי</Label>
              )}
            </Stack>
          )}

          {/* Org-mates' notes/meetings/tasks on this listing. */}
          <ListingOrgActivityIndicator listingId={listingId} />

          {/* flexShrink:0 + auto inline-start margin: end-aligned while there is room,
              on their own line when there isn't. Never clipped either way. */}
          <Stack
            direction="row"
            alignItems="center"
            gap={0.25}
            sx={{ flexShrink: 0, marginInlineStart: "auto" }}
          >
            {pendingRemoval ? (
              <Button
                size="small"
                color="warning"
                variant="outlined"
                startIcon={<Replay sx={{ fontSize: 16 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onUndoRemove(listingId);
                }}
              >
                החזרה
              </Button>
            ) : (
              <>
                {watermarkTracked && (
                  <Tooltip title={watermarkTooltip}>
                    <IconButton
                      size="small"
                      color={watermarkActionColor}
                      onClick={(e) => {
                        e.stopPropagation();
                        onManageWatermarks(listingId);
                      }}
                    >
                      <AutoFixHigh sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="וואטסאפ על הנכס">
                  <IconButton
                    size="small"
                    sx={{ color: "#25D366" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleWhatsApp();
                    }}
                  >
                    <WhatsApp sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="עריכת הנכס במיניסייט">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(listing);
                    }}
                  >
                    <Edit sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="הסרה מהמיניסייט">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(listingId);
                    }}
                  >
                    <DeleteOutline sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}

// ── the sub-tab ─────────────────────────────────────────────────────────────────

export default function MinisiteSubTab({
  contact,
  favorites,
  views,
  listings,
  watermarkStatuses,
  onSwitchToAdd
}: MinisiteSubTabProps): JSX.Element {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { userSettings } = useFirestore();
  /** The one gate on showing watermark-free photos — per realtor, accepted once, forever. */
  const termsAccepted = Boolean(userSettings?.watermarkTermsAcceptedAt);
  // The SURFACE, not the window — see `contactSurface.tsx`. The two things below that drop
  // on a narrow layout (a 40px decorative globe, an unbreakable 280px URL) are exactly the
  // ones a 420px side panel has no room for, and a viewport query kept both.
  const isNarrow = useContactSurfaceDown("sm");

  const [activeFilter, setActiveFilter] = useState<EngagementFilterId>("all");
  const [query, setQuery] = useState("");
  const [quickViewListing, setQuickViewListing] = useState<MatchHit | null>(null);
  const [editListing, setEditListing] = useState<MatchHit | null>(null);
  // Single-listing watermark studio, opened straight from a card (no need to go through
  // the edit dialog). The studio itself supports whole-listing AND image-by-image removal.
  const [watermarkListingId, setWatermarkListingId] = useState<string | null>(null);

  const watermarkStatusById = useMemo(
    () => new Map((watermarkStatuses ?? []).map((status) => [status.listingId, status])),
    [watermarkStatuses]
  );

  // ── undo-able removal (commit-after-window) ───────────────────────────────────
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const pendingTimeoutsRef = useRef(new Map<string, number>());
  const mountedRef = useRef(true);

  const commitRemoval = (listingId: string): void => {
    pendingTimeoutsRef.current.delete(listingId);
    if (mountedRef.current) {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(listingId);
        return next;
      });
    }
    removeListingFromContactMinisite(contact.id, listingId).catch((err) => {
      console.error("MatchesTab: failed to remove listing from minisite", err);
      if (mountedRef.current) {
        enqueueSnackbar("הסרת הנכס נכשלה, נסו שוב", {
          variant: "error",
          autoHideDuration: 4000
        });
      }
    });
  };

  const handleRemove = (listingId: string): void => {
    if (pendingTimeoutsRef.current.has(listingId)) return;
    const timeoutId = window.setTimeout(() => commitRemoval(listingId), REMOVE_UNDO_WINDOW_MS);
    pendingTimeoutsRef.current.set(listingId, timeoutId);
    setPendingIds((prev) => new Set(prev).add(listingId));
  };

  const handleUndoRemove = (listingId: string): void => {
    const timeoutId = pendingTimeoutsRef.current.get(listingId);
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    pendingTimeoutsRef.current.delete(listingId);
    setPendingIds((prev) => {
      const next = new Set(prev);
      next.delete(listingId);
      return next;
    });
  };

  // Flush pending removals immediately when the sub-tab unmounts (sub-tab switch /
  // page close) or the browser page hides — the undo window must never lose a removal.
  useEffect(() => {
    mountedRef.current = true;
    const flushAll = (): void => {
      const entries = Array.from(pendingTimeoutsRef.current.entries());
      pendingTimeoutsRef.current.clear();
      entries.forEach(([listingId, timeoutId]) => {
        window.clearTimeout(timeoutId);
        void removeListingFromContactMinisite(contact.id, listingId).catch(() => undefined);
      });
    };
    window.addEventListener("pagehide", flushAll);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("pagehide", flushAll);
      flushAll();
    };
  }, [contact.id]);

  const firstName = getUserFirstNameFallBackToPhoneNumber(contact);
  const favoriteListingIds = favorites?.listingIds;
  const listingIds = useMemo(() => favoriteListingIds ?? [], [favoriteListingIds]);
  const minisiteUrl = favorites?.id ? buildMinisiteUrl(favorites.id) : "";

  // ── stats ─────────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    let viewed = 0;
    let liked = 0;
    let disliked = 0;
    for (const id of listingIds) {
      const e = getListingEngagement(id, favorites, views);
      if (e.viewed) viewed += 1;
      if (e.liked) liked += 1;
      if (e.disliked) disliked += 1;
    }
    return { viewed, unviewed: listingIds.length - viewed, liked, disliked };
  }, [listingIds, favorites, views]);

  const totalViews = (views?.totalPageViews ?? 0) + (views?.totalListingDetailViews ?? 0);
  const lastVisitAt = useMemo(() => {
    const candidates = [views?.lastPageViewAt, views?.lastListingDetailViewAt]
      .filter(Boolean)
      .map((d) => (d instanceof Date ? d : new Date(d as unknown as string)));
    if (candidates.length === 0) return undefined;
    return new Date(Math.max(...candidates.map((d) => d.getTime())));
  }, [views?.lastPageViewAt, views?.lastListingDetailViewAt]);

  // ── filtering (engagement + fuzzy search) ─────────────────────────────────────
  const visibleListings = useMemo(() => {
    const all = listings ?? [];
    return all.filter((listing) => {
      const e = getListingEngagement(listing.objectID, favorites, views);
      if (!engagementMatchesFilter(e, activeFilter)) return false;
      if (query.trim() && !fuzzyMatches(query, buildListingSearchHaystack(listing, favorites))) {
        return false;
      }
      return true;
    });
  }, [listings, favorites, views, activeFilter, query]);

  // The quick view mirrors what the customer actually sees: clean versions display
  // when THIS user removed them (per-user gate, live data — same rule as the card
  // thumbs), swapping the URLs AND translating the hidden-picture lists — the exact
  // util pair the public client uses (hidden lists store ORIGINAL urls).
  const quickViewDisplay = useMemo(() => {
    if (!quickViewListing || !favorites) {
      return { listing: quickViewListing, favorites };
    }
    const status = watermarkStatusById.get(quickViewListing.objectID);
    const gatedListing: MatchHit = {
      ...quickViewListing,
      watermarkFreeImages: termsAccepted ? status?.watermarkFreeImages : []
    };
    /*
     * The favourites doc is handed over UNTRANSLATED. Its hidden-picture lists are keyed by
     * ORIGINAL urls, and that is the space the consumer resolves visibility in before substituting
     * clean urls (`resolveListingPictures`). Translating the stored list here — which is what
     * `applyWatermarkFreeImagesToFavoriteDoc` did — meant two independent decisions had to agree
     * about a privacy-critical list, and any ordering where the swap won rendered a hidden photo.
     */
    return { listing: applyWatermarkFreeImagesToListing(gatedListing), favorites };
  }, [quickViewListing, favorites, watermarkStatusById, termsAccepted]);

  const toggleFilter = (id: EngagementFilterId): void =>
    setActiveFilter((prev) => (prev === id && id !== "all" ? "all" : id));

  const highlightPalette =
    activeFilter === "all"
      ? undefined
      : ENGAGEMENT_FILTERS.find((f) => f.id === activeFilter)?.palette;

  // True cold start (no cached favorites yet): subtle skeletons, never a page loader.
  if (favorites === undefined) {
    return (
      <Box sx={{ pt: 2 }}>
        <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 1, mb: 2 }} />
        <Box sx={gridSx}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={230} sx={{ borderRadius: 1 }} />
          ))}
        </Box>
      </Box>
    );
  }

  const hasMinisite = Boolean(favorites?.id) && listingIds.length > 0;
  const isFiltering = activeFilter !== "all" || Boolean(query.trim());

  return (
    <Box sx={{ pt: 2 }}>
      {hasMinisite && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          flexWrap="wrap"
          sx={{
            p: 1.5,
            mb: 2,
            rowGap: 1,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.info.main, 0.08)
          }}
        >
          {/* decorative globe — dropped when narrow so the card stays a tight two-liner */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: isNarrow ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "info.main",
              bgcolor: alpha(theme.palette.info.main, 0.16)
            }}
          >
            <Language fontSize="small" />
          </Box>
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                המיניסייט של {firstName}
              </Typography>
              {/* The raw URL is long and unbreakable — hidden on mobile (Haim mobile
                  reject); copy / share / preview cover every use of it there. */}
              <Typography
                component="a"
                href={minisiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                dir="ltr"
                noWrap
                sx={{
                  color: "info.main",
                  textDecoration: "none",
                  direction: "ltr",
                  minWidth: 0,
                  maxWidth: 280,
                  display: isNarrow ? "none" : "block"
                }}
              >
                {minisiteUrl.replace(/^https?:\/\//, "")}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {listingIds.length} נכסים · {totalViews} צפיות סה״כ
              {lastVisitAt ? ` · ביקור אחרון: ${formatRelativeDayTime(lastVisitAt)}` : ""}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            <CopyClipboard value={minisiteUrl} iconSize={18} />
            <Button
              size="small"
              variant="contained"
              startIcon={<WhatsApp />}
              sx={{ bgcolor: "#25D366", "&:hover": { bgcolor: "#1DA851" } }}
              onClick={() =>
                openWhatsAppShare(contact, buildMinisiteShareMessage(contact, minisiteUrl))
              }
            >
              שתף בוואטסאפ
            </Button>
            <Tooltip title="תצוגה מקדימה">
              <IconButton
                size="small"
                color="info"
                href={minisiteUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <OpenInNew sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      )}

      {/* stats mini-cards — clicking applies the matching filter + highlights cards */}
      {hasMinisite && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
          <StatCard
            label="נצפו"
            count={stats.viewed}
            palette="info"
            active={activeFilter === "viewed"}
            onClick={() => toggleFilter("viewed")}
          />
          <StatCard
            label="טרם נצפו"
            count={stats.unviewed}
            palette="warning"
            active={activeFilter === "unviewed"}
            onClick={() => toggleFilter("unviewed")}
          />
          <StatCard
            label="אהבו"
            count={stats.liked}
            palette="success"
            active={activeFilter === "liked"}
            onClick={() => toggleFilter("liked")}
          />
          <StatCard
            label="לא רלוונטי"
            count={stats.disliked}
            palette="error"
            active={activeFilter === "disliked"}
            onClick={() => toggleFilter("disliked")}
          />
        </Box>
      )}

      {/* filter chips + fuzzy search */}
      {hasMinisite && (
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {ENGAGEMENT_FILTERS.map(({ id, label, palette }) => {
              const active = activeFilter === id;
              // Active chip takes its filter's semantic color (הכל falls back to primary).
              const chip = theme.palette[palette ?? "primary"];
              return (
                <ButtonBase
                  key={id}
                  onClick={() => toggleFilter(id)}
                  sx={{
                    px: 1.25,
                    py: 0.4,
                    borderRadius: 999,
                    typography: "caption",
                    fontWeight: active ? 600 : 500,
                    color: active ? chip.dark : "text.secondary",
                    bgcolor: active ? alpha(chip.main, 0.12) : "transparent",
                    border: `1px solid ${active ? alpha(chip.main, 0.4) : theme.palette.divider}`
                  }}
                >
                  {label}
                </ButtonBase>
              );
            })}
          </Stack>
          <Box sx={{ flex: 1 }} />
          <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="חיפוש לפי כתובת, תיאור או הערה..."
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                </InputAdornment>
              )
            }}
          />
        </Stack>
      )}

      <Box sx={gridSx}>
        {listingIds.length > 0 && listings === undefined
          ? listingIds
              .slice(0, 6)
              .map((id) => (
                <Skeleton key={id} variant="rectangular" height={230} sx={{ borderRadius: 1 }} />
              ))
          : visibleListings.map((listing) => (
              <MinisiteListingCard
                key={listing.objectID}
                listing={listing}
                contact={contact}
                favorites={favorites as FavoriteListingsOfContact}
                views={views}
                minisiteUrl={minisiteUrl}
                highlightPalette={highlightPalette}
                pendingRemoval={pendingIds.has(listing.objectID)}
                watermarkStatus={watermarkStatusById.get(listing.objectID)}
                onOpen={setQuickViewListing}
                onEdit={setEditListing}
                onRemove={handleRemove}
                onUndoRemove={handleUndoRemove}
                onManageWatermarks={setWatermarkListingId}
              />
            ))}

        {/* dashed ghost tile → switch to the add sub-tab (mock mi-ghost) */}
        {!isFiltering && (
          <ButtonBase
            onClick={onSwitchToAdd}
            sx={{
              minHeight: listingIds.length > 0 ? 230 : 140,
              borderRadius: 1,
              border: `1.5px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              color: "primary.main",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) }
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.12)
              }}
            >
              <Add fontSize="small" />
            </Box>
            <Typography variant="subtitle2">הוספת נכסים למיניסייט</Typography>
          </ButtonBase>
        )}
      </Box>

      {isFiltering && visibleListings.length === 0 && (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="subtitle2" color="text.secondary">
            אין נכסים תואמים לסינון
          </Typography>
          <Typography variant="caption" color="text.secondary">
            נסו סינון אחר או נקו את החיפוש
          </Typography>
        </Box>
      )}

      {!hasMinisite && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
          עדיין לא נוספו נכסים ללקוח — הוסיפו נכסים והמיניסייט האישי ייווצר אוטומטית עם קישור
          לשיתוף.
        </Typography>
      )}

      {/* quick view — actions: WhatsApp follow-up + edit. Displays the watermark-real
          copies; the action callbacks below keep the ORIGINAL listing (edits/writes
          are keyed by original image URLs). */}
      <ListingQuickViewDialog
        open={Boolean(quickViewListing)}
        listing={quickViewDisplay.listing}
        favorites={quickViewDisplay.favorites}
        onClose={() => setQuickViewListing(null)}
        actions={
          quickViewListing && (
            <>
              <Button
                size="small"
                startIcon={<Edit />}
                color="inherit"
                onClick={() => {
                  setEditListing(quickViewListing);
                  setQuickViewListing(null);
                }}
              >
                עריכה במיניסייט
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<WhatsApp />}
                sx={{ bgcolor: "#25D366", "&:hover": { bgcolor: "#1DA851" } }}
                onClick={() => {
                  const addressLine = buildListingAddressLine(quickViewListing);
                  const url = favorites?.id
                    ? buildMinisiteListingUrl(favorites.id, quickViewListing.objectID)
                    : minisiteUrl;
                  openWhatsAppShare(
                    contact,
                    buildListingFollowUpMessage(contact, addressLine, url)
                  );
                }}
              >
                וואטסאפ ללקוח
              </Button>
            </>
          )
        }
      />

      <ListingEditDialog
        open={Boolean(editListing)}
        contact={contact}
        listing={editListing}
        favorites={favorites}
        onClose={() => setEditListing(null)}
      />

      {/* The EXISTING per-listing removal studio, reached in one click from a card.
          Mounted only while open so its internal state resets between listings. */}
      {watermarkListingId && (
        <WatermarkRemovalDialog
          open
          listingId={watermarkListingId}
          onClose={() => setWatermarkListingId(null)}
        />
      )}
    </Box>
  );
}

// Column count follows the CONTAINER, not the viewport — the fix for the "cut cards"
// report. MUI breakpoints measure the WINDOW, but this grid sits inside the customer
// page's TabCard, behind a 280px sidebar (permanent from lg), a Container (24px) and
// the card's own 20px padding. Note the width is NOT monotonic in the window: a
// 1024px window gives this grid 936px, while a 1200px one — where the sidebar becomes
// permanent — gives only 832px. A viewport breakpoint cannot express that, which is
// why widening the browser could make the bug appear rather than go away.
//
// The card sets overflow:hidden, which zeroes a grid item's automatic minimum size, so
// the tracks never grow and the grid never overflows — the card clips its OWN content
// instead, silently. Measured across the widths this tab actually produces, old grid:
//    768px window → 3 × 219px → every card lost action icons off the edge
//    900px window → 3 × 263px → every engagement pill squeezed under its own text
//   1200px window → 3 × 269px → same (the most common desktop width)
// After: 2 × 400px / 2 × 410px / 3 × 296px+ — clean at every width tested.
//
// `min(100%, …)` keeps the floor from overflowing a container narrower than it (small
// phones); `& > *` minWidth:0 is the backstop should a card ever grow content that
// refuses to shrink.
const MIN_CARD_WIDTH_PX = 280;

const gridSx = {
  display: "grid",
  gap: 1.5,
  gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${MIN_CARD_WIDTH_PX}px), 1fr))`,
  "& > *": { minWidth: 0 }
} as const;
